import type { MultiLocalPrinterCapabilities } from "./localPrinterCapabilities"
import type { PrioritySnapshot } from "./localPrintPriority"
import type {
  CompleteLocalPrintDeliveryDTO,
  CompleteLocalPrintTaskDTO,
  LocalPrintDeliveryTaskVO,
  LocalPrintTaskVO,
  PrintScene,
} from "@/api/business/types/printing"
import type { LocalPrintDeviceContext } from "@/utils/hardwareBridge"
import {
  claimLocalPrintBatch,
  claimLocalPrintTasks,
  claimLocalPrintTasksV2,
  claimLocalPrintTasksV3,
  claimLocalPrintTasksV4,
  completeLocalPrintDelivery,
  completeLocalPrintTask,
} from "@/api/business/printing"
import { isHqScope } from "@/router/hqAccess"
import { useUserStore } from "@/store/modules/user"
import { useViewScopeStoreHook } from "@/store/modules/viewScope"
import { hardwareBridge } from "@/utils/hardwareBridge"
import {
  HardwareBridgeFeatureUnsupportedError,
  isPrintOutcomeUnknownError,
  isPrintReconcileRequiredError,
} from "@/utils/hardwareBridgeErrors"
import { buildLocalPrinterReceiptStyleForPrinter } from "@/utils/localPrinterReceiptStyle"
import { generateOrderReceipt } from "@/utils/receiptGenerator"
import { fetchCheckoutReceiptPrintContent } from "./checkoutReceiptTask"
import { getLocalPrintClientId } from "./localPrintClientId"
import { invalidateLocalPrinterCapabilities, loadMultiLocalPrinterCapabilities } from "./localPrinterCapabilities"
import { invalidateLocalPrintPriority, LocalPrintScopeChangedError, publishLocalPrintBatchPriority, refreshLocalPrintPriority, startLocalPrintPrioritySync } from "./localPrintPriority"
import { withLocalPrintTabLock } from "./localPrintTabCoordinator"
import { resolveLocalPrintTaskId } from "./localPrintTaskUtils"

const COMPLETE_RETRY_ATTEMPTS = 3
const PRINT_OUTCOME_UNKNOWN_RETRY_ATTEMPTS = 3
const COMPLETE_RETRY_DELAY_MS = 300
const FALLBACK_POLL_INTERVAL_MS = 30_000
const CLAIM_BATCH_SIZE = 1
const MAX_TASKS_PER_SCENE_PER_POLL = 20
const LOCAL_PRINT_TASK_CREATED_EVENT = "local-print-task-created"
const LOCAL_PRINT_RECONCILE_REQUIRED = "LOCAL_PRINT_RECONCILE_REQUIRED"
const SUPPORTED_PRINT_SCENES: PrintScene[] = ["KITCHEN_ORDER", "CHECKOUT_RECEIPT"]
const RECONCILE_REQUIRED_ERROR_MESSAGE = "LOCAL_PRINT_RECONCILE_REQUIRED"

let claimRetryScope = ""
let claimRetryAfter = 0
let claimFailures = 0
let polling = false
let listenerRegistered = false
let rerunRequested = false
let fallbackTimer: number | null = null
let stopPrioritySync: (() => void) | null = null
let schedulerGeneration = 0
const unchangedScope = () => {}
// 只保留助手已确认成功、服务端尚未确认的回执；不能因为补查变频再次提交纸张。
const printedAwaitingAck = new Set<string>()

function pendingReceiptKey(kind: string, id: number, token: string | null | undefined, deviceId = "") {
  const user = useUserStore()
  return JSON.stringify([user.token, user.shopId, useViewScopeStoreHook().activeScopeId, kind, id, token, deviceId])
}

export interface LocalPrintTaskNotificationPayload {
  scene?: string
  count?: number
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

export function parseLocalPrintTaskNotificationPayload(data: unknown): LocalPrintTaskNotificationPayload | null {
  if (data == null || data === "") return {}

  let payload = data
  if (typeof data === "string") {
    try {
      payload = JSON.parse(data)
    } catch {
      payload = data
    }
  }

  if (typeof payload === "string") {
    return payload ? { scene: payload } : {}
  }
  if (!isRecord(payload)) return null

  const result: LocalPrintTaskNotificationPayload = {}
  if (typeof payload.scene === "string" && payload.scene) {
    result.scene = payload.scene
  }
  if (typeof payload.count === "number" && Number.isFinite(payload.count)) {
    result.count = payload.count
  }
  return result
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message
  if (typeof error === "string") return error
  return "本地打印失败"
}

function delay(ms: number) {
  return new Promise(resolve => window.setTimeout(resolve, ms))
}

function isHeadquarterScopeActive() {
  const viewScopeStore = useViewScopeStoreHook()
  return isHqScope(viewScopeStore.activeScope, viewScopeStore.activeScopeId)
}

async function completeLocalPrintTaskWithRetry(taskId: number, data: CompleteLocalPrintTaskDTO, assertScope = unchangedScope) {
  let lastError: unknown
  for (let attempt = 1; attempt <= COMPLETE_RETRY_ATTEMPTS; attempt++) {
    try {
      assertScope()
      const response = await completeLocalPrintTask(taskId, data)
      if (response.code !== 0 || response.data !== true) {
        throw new Error(response.msg || "确认本地打印结果失败")
      }
      return response
    } catch (error) {
      if (error instanceof LocalPrintScopeChangedError) throw error
      lastError = error
      if (attempt < COMPLETE_RETRY_ATTEMPTS) {
        await delay(COMPLETE_RETRY_DELAY_MS * attempt)
      }
    }
  }
  throw lastError
}

async function completeLocalPrintDeliveryWithRetry(
  deliveryId: number,
  data: CompleteLocalPrintDeliveryDTO,
  assertScope: () => void,
) {
  let lastError: unknown
  for (let attempt = 1; attempt <= COMPLETE_RETRY_ATTEMPTS; attempt++) {
    try {
      assertScope()
      const response = await completeLocalPrintDelivery(deliveryId, data)
      if (response.code !== 0 || response.data !== true) {
        throw new Error(response.msg || "确认本地打印设备投递结果失败")
      }
      return response
    } catch (error) {
      if (error instanceof LocalPrintScopeChangedError) throw error
      lastError = error
      if (attempt < COMPLETE_RETRY_ATTEMPTS) {
        await delay(COMPLETE_RETRY_DELAY_MS * attempt)
      }
    }
  }
  throw lastError
}

function buildCompletePayload(task: LocalPrintTaskVO, success: boolean, errorMessage?: string): CompleteLocalPrintTaskDTO {
  return {
    success,
    ...(errorMessage ? { errorMessage } : {}),
    ...(task.claimToken ? { claimToken: task.claimToken } : {}),
  }
}

async function printClaimedTask(
  task: LocalPrintTaskVO | LocalPrintDeliveryTaskVO,
  taskId: number,
  stablePrintJobId?: string,
  assertScope = unchangedScope,
) {
  assertScope()
  const userStore = useUserStore()
  const localPrinterKey = task.localPrinterKey || "RECEIPT"
  const style = buildLocalPrinterReceiptStyleForPrinter(userStore.printSetting, localPrinterKey)
  if (task.scene === "CHECKOUT_RECEIPT") {
    const orderId = Number(task.sourceId)
    if (!Number.isSafeInteger(orderId) || orderId <= 0) {
      throw new Error("结账单打印任务缺少有效订单ID")
    }
    const content = await fetchCheckoutReceiptPrintContent(orderId)
    assertScope()
    await hardwareBridge.printReceipt(
      generateOrderReceipt(content, style),
      stablePrintJobId || `checkout-receipt-${taskId}`,
      localPrinterKey,
      style,
    )
    return
  }

  await hardwareBridge.printPlainText(
    task.content || "",
    style,
    stablePrintJobId || `kitchen-order-${taskId}-${task.claimToken || "legacy"}`,
    localPrinterKey,
  )
}

function buildDeliveryCompletePayload(
  task: LocalPrintDeliveryTaskVO,
  deviceId: string,
  success: boolean,
  errorMessage?: string,
): CompleteLocalPrintDeliveryDTO {
  return {
    deviceId,
    claimToken: task.claimToken,
    success,
    ...(errorMessage ? { errorMessage } : {}),
  }
}

async function processClaimedDelivery(task: LocalPrintDeliveryTaskVO, deviceId: string, assertScope: () => void) {
  assertScope()
  const taskId = resolveLocalPrintTaskId(task)
  if (!taskId
    || !Number.isSafeInteger(task.deliveryId)
    || task.deliveryId <= 0
    || !task.printJobId
    || !task.claimToken) {
    throw new TypeError("本地打印设备投递格式无效")
  }

  const receiptKey = pendingReceiptKey("delivery", task.deliveryId, task.claimToken, deviceId)
  try {
    if (!printedAwaitingAck.has(receiptKey)) {
      await printClaimedTask(task, taskId, task.printJobId, assertScope)
      printedAwaitingAck.add(receiptKey)
    }
  } catch (error) {
    if (error instanceof LocalPrintScopeChangedError) throw error
    console.warn("Local device delivery print failed", error)
    const errorMessage = isPrintOutcomeUnknownError(error) || isPrintReconcileRequiredError(error)
      ? LOCAL_PRINT_RECONCILE_REQUIRED
      : getErrorMessage(error)
    try {
      await completeLocalPrintDeliveryWithRetry(
        task.deliveryId,
        buildDeliveryCompletePayload(task, deviceId, false, errorMessage),
        assertScope,
      )
    } catch (ackError) {
      console.warn("Local device delivery failure ack failed", ackError)
    }
    return false
  }

  try {
    await completeLocalPrintDeliveryWithRetry(
      task.deliveryId,
      buildDeliveryCompletePayload(task, deviceId, true),
      assertScope,
    )
    printedAwaitingAck.delete(receiptKey)
    return true
  } catch (ackError) {
    console.warn("Local device delivery success ack failed", ackError)
    return false
  }
}

async function processClaimedTask(task: LocalPrintTaskVO, assertScope = unchangedScope, reconcileUnknown = false) {
  assertScope()
  const taskId = resolveLocalPrintTaskId(task)
  if (!taskId) return false
  const receiptKey = pendingReceiptKey("task", taskId, task.claimToken)

  for (let attempt = 1; !printedAwaitingAck.has(receiptKey) && attempt <= PRINT_OUTCOME_UNKNOWN_RETRY_ATTEMPTS; attempt++) {
    try {
      await printClaimedTask(task, taskId, undefined, assertScope)
      printedAwaitingAck.add(receiptKey)
      break
    } catch (error) {
      if (error instanceof LocalPrintScopeChangedError) throw error
      if (isPrintReconcileRequiredError(error)) {
        console.warn("Local print requires manual reconciliation", error)
        try {
          await completeLocalPrintTaskWithRetry(
            taskId,
            buildCompletePayload(task, false, RECONCILE_REQUIRED_ERROR_MESSAGE),
            assertScope,
          )
          return true
        } catch (ackError) {
          console.warn("Local print reconciliation ack failed", ackError)
          return false
        }
      }
      if (isPrintOutcomeUnknownError(error)) {
        console.warn(
          `Local print outcome unknown; retrying the same helper job id (${attempt}/${PRINT_OUTCOME_UNKNOWN_RETRY_ATTEMPTS})`,
          error,
        )
        if (attempt < PRINT_OUTCOME_UNKNOWN_RETRY_ATTEMPTS) continue

        if (reconcileUnknown) {
          // 不能确认出纸的单据交给人工核对，不转给别的电脑，也不堵住后续新单。
          await completeLocalPrintTaskWithRetry(
            taskId,
            buildCompletePayload(task, false, RECONCILE_REQUIRED_ERROR_MESSAGE),
            assertScope,
          )
          return true
        }

        // The helper may already have submitted the physical print. Marking the backend
        // task failed here would make a later manual reprint use a new id and could print twice.
        console.warn("Local print outcome remains unknown; leaving the backend claim unresolved", error)
        return false
      }

      console.warn("Local print failed", error)
      try {
        await completeLocalPrintTaskWithRetry(taskId, buildCompletePayload(task, false, getErrorMessage(error)), assertScope)
      } catch (ackError) {
        console.warn("Local print failure ack failed", ackError)
      }
      return false
    }
  }

  try {
    await completeLocalPrintTaskWithRetry(taskId, buildCompletePayload(task, true), assertScope)
    printedAwaitingAck.delete(receiptKey)
    return true
  } catch (ackError) {
    console.warn("Local print success ack failed", ackError)
    return false
  }
}

async function claimAndProcessScene(scene: PrintScene, capabilities: MultiLocalPrinterCapabilities, assertScope: () => void) {
  let processedTaskCount = 0
  while (processedTaskCount < MAX_TASKS_PER_SCENE_PER_POLL) {
    assertScope()
    const clientId = getLocalPrintClientId()
    const res = capabilities.enabled
      ? await claimLocalPrintTasksV2(
        { localPrinterKeys: capabilities.localPrinterKeys },
        scene,
        CLAIM_BATCH_SIZE,
        clientId,
      )
      : await claimLocalPrintTasks(scene, CLAIM_BATCH_SIZE, clientId)
    assertScope()
    if (res.code !== 0) {
      throw new Error(res.msg || `领取${scene}本地打印任务失败`)
    }
    if (!Array.isArray(res.data) || res.data.length === 0) return

    for (const task of res.data) {
      const shouldContinue = await processClaimedTask(task, assertScope)
      processedTaskCount++
      if (!shouldContinue) return
    }
  }
  rerunRequested = true
}

async function claimAndProcessSceneV4(scene: PrintScene, snapshot: PrioritySnapshot) {
  if (!snapshot.device?.localPrinterKeys.length) return
  for (let count = 0; count < MAX_TASKS_PER_SCENE_PER_POLL; count++) {
    snapshot.assertCurrentScope()
    const response = await claimLocalPrintTasksV4(snapshot.device, scene)
    snapshot.assertCurrentScope()
    if (response.code !== 0) throw new Error(response.msg || "领取优先打印任务失败")
    if (!Array.isArray(response.data)) throw new TypeError("优先打印任务响应格式无效")
    if (!response.data.length) return
    for (const task of response.data) {
      if (!task.claimToken || task.scene !== scene) throw new TypeError("优先打印任务缺少领取凭据或场景错误")
      if (!(await processClaimedTask(task, snapshot.assertCurrentScope, true))) return
    }
  }
  rerunRequested = true
}

async function claimAndProcessBatch(scenes: PrintScene[], snapshot: PrioritySnapshot) {
  if (!snapshot.device?.localPrinterKeys.length) return
  for (let count = 0; count < MAX_TASKS_PER_SCENE_PER_POLL; count++) {
    snapshot.assertCurrentScope()
    const response = await claimLocalPrintBatch({ ...snapshot.device, scenes })
    snapshot.assertCurrentScope()
    if (response.code !== 0) throw new Error(response.msg || "领取优先打印任务失败")
    const tasks = response.data?.tasks
    if (!Array.isArray(tasks) || tasks.length > scenes.length) throw new TypeError("批量打印任务响应格式无效")
    publishLocalPrintBatchPriority(response.data.priority, snapshot.assertCurrentScope)
    if (!tasks.length) return
    const seen = new Set<string>()
    // 先校验整批，避免畸形响应部分出纸。
    for (const task of tasks) {
      if (!task.claimToken || !task.scene || !scenes.includes(task.scene as PrintScene)
        || seen.has(task.scene) || task.printTargetType !== "LOCAL") throw new TypeError("批量打印任务凭据或场景无效")
      if (!Number.isSafeInteger(task.merchantPrintTaskId) || Number(task.merchantPrintTaskId) <= 0) throw new TypeError("批量打印任务ID无效")
      seen.add(task.scene)
    }
    let canContinue = true
    for (const task of tasks) {
      snapshot.assertCurrentScope()
      if (!(await processClaimedTask(task as LocalPrintTaskVO, snapshot.assertCurrentScope, true))) canContinue = false
    }
    if (!canContinue) return
  }
  rerunRequested = true
}

interface ActiveLocalPrintDeviceContext extends LocalPrintDeviceContext {
  activationTaskId: number
}

async function loadActiveLocalPrintDeviceContext(
  shopId: string,
  capabilities: MultiLocalPrinterCapabilities,
  assertScope: () => void,
): Promise<ActiveLocalPrintDeviceContext> {
  let context = await hardwareBridge.getLocalPrintDeviceContext(shopId)
  assertScope()
  if (context.activationTaskId == null) {
    if (!context.activationToken) {
      throw new HardwareBridgeFeatureUnsupportedError("本地打印助手版本不支持安全设备激活")
    }
    const initializingDeviceId = context.deviceId
    const initialization = await claimLocalPrintTasksV3({
      deviceId: context.deviceId,
      activationTaskId: null,
      localPrinterKeys: capabilities.localPrinterKeys,
    }, "KITCHEN_ORDER", CLAIM_BATCH_SIZE)
    assertScope()
    const activationTaskId = initialization.data?.activationTaskId
    if (initialization.code !== 0
      || !Number.isSafeInteger(activationTaskId)
      || Number(activationTaskId) < 0
      || !Array.isArray(initialization.data?.tasks)
      || initialization.data.tasks.length !== 0) {
      throw new Error(initialization.msg || "初始化本地打印设备接入游标失败")
    }
    context = await hardwareBridge.activateLocalPrintDeviceContext(
      shopId,
      Number(activationTaskId),
      context.activationToken,
    )
    assertScope()
    if (context.deviceId !== initializingDeviceId) {
      throw new TypeError("本地打印助手设备身份在初始化期间发生变化")
    }
  }
  if (!Number.isSafeInteger(context.activationTaskId) || Number(context.activationTaskId) < 0) {
    throw new TypeError("本地打印助手缺少有效的设备接入游标")
  }
  return context as ActiveLocalPrintDeviceContext
}

async function claimAndProcessSceneV3(
  scene: PrintScene,
  capabilities: MultiLocalPrinterCapabilities,
  context: ActiveLocalPrintDeviceContext,
  assertScope: () => void,
) {
  let processedTaskCount = 0
  while (processedTaskCount < MAX_TASKS_PER_SCENE_PER_POLL) {
    assertScope()
    const response = await claimLocalPrintTasksV3({
      deviceId: context.deviceId,
      activationTaskId: context.activationTaskId,
      localPrinterKeys: capabilities.localPrinterKeys,
    }, scene, CLAIM_BATCH_SIZE)
    assertScope()
    if (response.code !== 0) {
      throw new Error(response.msg || `领取${scene}本地打印设备投递失败`)
    }
    if (response.data?.activationTaskId !== context.activationTaskId
      || !Array.isArray(response.data.tasks)) {
      throw new TypeError("本地打印设备投递响应格式无效")
    }
    if (response.data.tasks.length === 0) return

    for (const task of response.data.tasks) {
      const shouldContinue = await processClaimedDelivery(task, context.deviceId, assertScope)
      processedTaskCount++
      if (!shouldContinue) return
    }
  }
  rerunRequested = true
}

async function finishKnownPrintReceipts(assertScope: () => void) {
  const user = useUserStore()
  const scope = useViewScopeStoreHook().activeScopeId
  for (const key of printedAwaitingAck) {
    const [token, shopId, scopeId, kind, id, claimToken, deviceId] = JSON.parse(key)
    if (token !== user.token || shopId !== user.shopId || scopeId !== (scope ?? null)) continue
    assertScope()
    if (kind === "delivery") await completeLocalPrintDeliveryWithRetry(id, { success: true, claimToken, deviceId }, assertScope)
    else await completeLocalPrintTaskWithRetry(id, { success: true, ...(claimToken ? { claimToken } : {}) }, assertScope)
    printedAwaitingAck.delete(key)
  }
}

export async function pollLocalPrintTasksOnce() {
  if (polling) return

  const userStore = useUserStore()
  if (!userStore.token || !userStore.shopId) return
  if (isHeadquarterScopeActive()) return

  const token = userStore.token
  const shopId = userStore.shopId
  const scopeId = useViewScopeStoreHook().activeScopeId
  const retryScope = JSON.stringify([token, shopId, scopeId])
  if (retryScope !== claimRetryScope) {
    claimRetryScope = retryScope
    claimFailures = 0
    claimRetryAfter = 0
  }
  if (Date.now() < claimRetryAfter) return
  const generation = schedulerGeneration
  const assertSession = () => {
    if (token !== userStore.token || shopId !== userStore.shopId
      || scopeId !== useViewScopeStoreHook().activeScopeId || isHeadquarterScopeActive()
      || generation !== schedulerGeneration) throw new LocalPrintScopeChangedError()
  }

  polling = true
  try {
    await withLocalPrintTabLock(String(shopId), async () => {
      assertSession()
      const refreshed = await refreshLocalPrintPriority(false)
      const priority = { ...refreshed, assertCurrentScope: () => {
        assertSession()
        refreshed.assertCurrentScope()
      } }
      priority.assertCurrentScope()
      if (priority.helperAvailable === false || priority.noLocalPrinters) {
        await finishKnownPrintReceipts(priority.assertCurrentScope)
        return
      }
      // 缓存用于复用后端同步；领取前仍确认本机助手连接，避免用旧在线快照领单。
      if (!(await hardwareBridge.canPrint())) {
        await finishKnownPrintReceipts(priority.assertCurrentScope)
        return
      }
      priority.assertCurrentScope()
      if (priority.configured) {
        if (!priority.device) return
        const scenes = SUPPORTED_PRINT_SCENES.filter(scene => scene !== "CHECKOUT_RECEIPT" || userStore.checkPermission("business_order_receipt"))
        if (priority.batchClaimSupported) await claimAndProcessBatch(scenes, priority)
        else for (const scene of scenes) await claimAndProcessSceneV4(scene, priority)
        // 切换前有效旧任务由上线交接门禁处理，正常轮次只执行一个协议。
        return
      }
      const capabilities = await loadMultiLocalPrinterCapabilities(
        `${userStore.token}:${userStore.shopId}:${useViewScopeStoreHook().activeScopeId ?? ""}`,
      )
      priority.assertCurrentScope()
      let deviceContext: ActiveLocalPrintDeviceContext | null = null
      if (capabilities.deviceDeliveryEnabled && capabilities.helperSupported) {
        try {
          deviceContext = await loadActiveLocalPrintDeviceContext(String(shopId), capabilities, priority.assertCurrentScope)
        } catch (error) {
          if (!(error instanceof HardwareBridgeFeatureUnsupportedError)) throw error
          console.info("Local print helper does not support device delivery; falling back to atomic legacy claim")
        }
      }

      const claimableScenes = userStore.checkPermission("business_order_receipt")
        ? SUPPORTED_PRINT_SCENES
        : SUPPORTED_PRINT_SCENES.filter(scene => scene !== "CHECKOUT_RECEIPT")
      for (const scene of claimableScenes) {
        try {
          if (deviceContext) {
            await claimAndProcessSceneV3(scene, capabilities, deviceContext, priority.assertCurrentScope)
          } else {
            await claimAndProcessScene(scene, capabilities, priority.assertCurrentScope)
          }
        } catch (error) {
          if (error instanceof LocalPrintScopeChangedError) throw error
          console.warn(`Poll ${scene} local print tasks failed`, error)
        }
      }
    })
    claimFailures = 0
    claimRetryAfter = 0
  } catch (error) {
    if (generation === schedulerGeneration && !(error instanceof LocalPrintScopeChangedError)) {
      claimFailures++
      claimRetryAfter = Date.now() + Math.min(60_000, 15_000 * 2 ** Math.min(claimFailures - 1, 2))
    }
    console.warn("Poll local print tasks failed", error)
  } finally {
    polling = false
  }
}

function runPollerTick() {
  const generation = schedulerGeneration
  if (polling) {
    rerunRequested = true
    return
  }
  pollLocalPrintTasksOnce().catch((error) => {
    console.warn("Poll local print tasks failed", error)
  }).finally(() => {
    if (generation !== schedulerGeneration) return
    if (!rerunRequested) return
    rerunRequested = false
    runPollerTick()
  })
}

function shouldClaimForNotification(payload?: LocalPrintTaskNotificationPayload | null) {
  return !payload?.scene || SUPPORTED_PRINT_SCENES.includes(payload.scene as PrintScene)
}

export function handleLocalPrintTaskNotification(payload?: LocalPrintTaskNotificationPayload | null) {
  if (!shouldClaimForNotification(payload)) return
  if (payload?.count === 0) {
    invalidateLocalPrintPriority()
    invalidateLocalPrinterCapabilities()
  }
  runPollerTick()
}

function handleLocalPrintTaskCreatedEvent(event: Event) {
  const payload = event instanceof CustomEvent ? event.detail as LocalPrintTaskNotificationPayload | undefined : undefined
  handleLocalPrintTaskNotification(payload)
}

export function notifyLocalPrintTasksReady(payload?: LocalPrintTaskNotificationPayload | null) {
  window.dispatchEvent(new CustomEvent(LOCAL_PRINT_TASK_CREATED_EVENT, { detail: payload }))
}

export function startLocalPrintTaskPoller() {
  if (!stopPrioritySync) stopPrioritySync = startLocalPrintPrioritySync(runPollerTick)
  if (!listenerRegistered) {
    window.addEventListener(LOCAL_PRINT_TASK_CREATED_EVENT, handleLocalPrintTaskCreatedEvent)
    listenerRegistered = true
  }
  if (fallbackTimer === null) {
    runPollerTick()
    fallbackTimer = window.setInterval(runPollerTick, FALLBACK_POLL_INTERVAL_MS)
  }
}

export function stopLocalPrintTaskPoller() {
  claimFailures = 0
  claimRetryAfter = 0
  schedulerGeneration++
  stopPrioritySync?.()
  stopPrioritySync = null
  rerunRequested = false
  if (fallbackTimer !== null) {
    window.clearInterval(fallbackTimer)
    fallbackTimer = null
  }
  if (listenerRegistered) {
    window.removeEventListener(LOCAL_PRINT_TASK_CREATED_EVENT, handleLocalPrintTaskCreatedEvent)
    listenerRegistered = false
  }
}
